/**
 * T-052: Sales integration health card.
 *
 * Read-only. Surfaces salesInternalClient config + sales_outbox
 * activity. The Sales-side metrics block is deferred until the
 * Sales repo's S.0 Bearer JWT auth ticket lands; we render greyed
 * placeholders so the operator knows what's coming.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getSalesIntegration } from '@/lib/pocketSettingsApi';
import type { PocketSalesIntegration } from '@/types/pocketSettings';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export function SalesIntegrationCard() {
  const [data, setData] = useState<PocketSalesIntegration | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSalesIntegration()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Non-fatal.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Sales integration</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          App↔Sales runtime config + outbox activity. Sales-side latency and
          auth-success metrics are deferred until the Sales repo's S.0
          ticket ships — placeholders below.
        </p>

        {!data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {/* Config tiles */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Tile label="Host" value={hostFromUrl(data.config.base_url)} mono />
              <Tile label="Timeout" value={`${data.config.timeout_ms}ms`} />
              <div className="rounded-md border border-border/50 p-4 bg-card">
                <div className="text-xs text-muted-foreground">Auth</div>
                <div className="mt-1.5">
                  {data.config.auth_configured ? (
                    <Badge variant="secondary">Configured</Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-amber-500/50 text-amber-500"
                    >
                      Not configured
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Outbox snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <Tile
                label="Delivered (24h)"
                value={String(data.outbox.delivered_24h)}
              />
              <Tile
                label="DLQ"
                value={String(data.outbox.dlq_count)}
                tone={data.outbox.dlq_count > 0 ? 'warn' : undefined}
              />
              <Tile
                label="Last delivered"
                value={formatDate(data.outbox.last_delivered_at)}
                small
              />
              <Tile
                label="Last failed"
                value={formatDate(data.outbox.last_failed_at)}
                small
              />
            </div>

            {/* Recent failures */}
            {data.outbox.recent_errors.length > 0 ? (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground">
                  Recent dead-lettered ({data.outbox.recent_errors.length})
                </summary>
                <div className="mt-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Last error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.outbox.recent_errors.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono text-xs">{row.endpoint}</TableCell>
                          <TableCell className="tabular-nums">{row.attempts}</TableCell>
                          <TableCell className="whitespace-nowrap tabular-nums">
                            {formatDate(row.failed_permanently_at)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                            {row.last_error || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            ) : null}

            {/* Deferred Sales-side metrics */}
            <div className="mt-6 rounded-md border border-dashed border-border/60 p-4 bg-muted/20">
              <div className="text-xs font-medium mb-2 text-muted-foreground">
                Pending Sales-side S.0
              </div>
              <div className="grid grid-cols-3 gap-4">
                <DeferredTile label="Bearer validation rate" />
                <DeferredTile label="Sales search p95" />
                <DeferredTile label="Sales rentals p95" />
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">
                {data.deferred_metrics.note ||
                  'Lights up once Sales-side Bearer auth + latency endpoints ship.'}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({
  label,
  value,
  tone,
  mono,
  small,
}: {
  label: string;
  value: string;
  tone?: 'warn';
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={
          (small ? 'text-sm ' : 'text-2xl ') +
          'font-semibold tabular-nums mt-1 ' +
          (tone === 'warn' ? 'text-destructive ' : '') +
          (mono ? 'font-mono' : '')
        }
      >
        {value}
      </div>
    </div>
  );
}

function DeferredTile({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-border/40 p-3 bg-card/50">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1 text-muted-foreground/40">
        —
      </div>
    </div>
  );
}
